import { Box, Skeleton, Typography } from '@mui/material';

import { proAccountThemeCoreUI } from '@orcha/micro-library-core-ui';
import { AppliedFilterChip } from '@orcha/micro-library-search-ui';
import React from 'react';
import { useSearch } from 'src/store';
import useSearchFilters from 'src/hooks/useSearchFilters';
import useTranslation from 'next-translate/useTranslation';

export const AppliedFilters = () => {
  const { appliedFilters, removeAppliedFilter, clearAppliedFilters } =
    useSearch();

  const { filters, isLoading } = useSearchFilters();

  const { t } = useTranslation('search');

  const handleDelete = (filterName: string, option: string) => () => {
    removeAppliedFilter(filterName, option);
  };

  const handleDeleteAll = () => {
    clearAppliedFilters();
  };

  const sortAlphabetically = (items: string[]) => {
    return items.sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1));
  };

  const getFilter = (filterName: string) => {
    return filters?.find((p) => p.filterName === filterName);
  };

  const getOptionLabel = (
    filterName: string,
    option: string,
    translationRequired?: boolean
  ) => {
    return translationRequired
      ? t(
          `filterOption.${filterName.toLocaleLowerCase()}.${option.toLocaleLowerCase()}`
        )
      : option;
  };

  const applicableFilters = filters?.filter(
    (filter) => filter.dataType !== 'Boolean'
  );

  const shouldShowClearAll =
    appliedFilters &&
    appliedFilters.some(
      (appliedFilter) =>
        appliedFilter.selectedOptions.length > 0 &&
        !!appliedFilter.filterName &&
        applicableFilters?.some(
          (filter) => filter.filterName === appliedFilter.filterName
        )
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: proAccountThemeCoreUI.spacing(2),
      }}
    >
      {shouldShowClearAll && (
        <AppliedFilterChip
          label={t('clearAllFiltersLabel')}
          onDelete={handleDeleteAll}
          variant="filled"
          data-testid="applied-filters-clear-all"
        />
      )}
      {appliedFilters?.map((filter, index) => {
        const { filterName, selectedOptions } = filter;
        const searchFilter = getFilter(filterName);
        if (searchFilter?.dataType === 'Boolean') {
          return;
        }
        if (isLoading) {
          return sortAlphabetically(selectedOptions).map((option) => {
            return (
              <Skeleton
                key={option}
                variant="rounded"
                sx={{ borderRadius: '16px' }}
                height={32}
                width={120}
                component="div"
              />
            );
          });
        }
        const filterChips = sortAlphabetically(selectedOptions).map(
          (option) => {
            return (
              <AppliedFilterChip
                key={`${filterName}-${option}`}
                label={getOptionLabel(
                  filterName,
                  option,
                  searchFilter?.isValueTranslationRequired
                )}
                onDelete={handleDelete(filterName, option)}
                variant="outlined"
                data-testid="applied-filter-chip"
                data-filter-chip-value={filterName}
              />
            );
          }
        );

        return (
          selectedOptions.length > 0 &&
          filterName && (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={`${filterName}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: proAccountThemeCoreUI.spacing(1),
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="body2">
                {t(`filterLabel.${filterName?.toLowerCase()}`)}:
              </Typography>
              {filterChips}
            </Box>
          )
        );
      })}
    </Box>
  );
};

export default AppliedFilters;
